import type { ReactNode } from "react";
import type { ColumnDef } from "@tanstack/react-table";
import type { TFunction } from "i18next";
import { Images } from "lucide-react";

import { fmtRelative } from "~/shared/lib/fmt";
import { Badge } from "~/shared/ui/badge";
import { MediaThumb } from "~/shared/ui/media-thumb";

import type { Element } from "../types";

export interface ElementColumnsOptions {
    /** Действия строки (меню «изменить / удалить») — последняя колонка. */
    renderActions?: (element: Element) => ReactNode;
}

/**
 * Колонки таблицы раздела «Элементы». Сортируемые колонки (`name`, `imagesCount`,
 * `createdAt`) сортируются на клиенте — бэк отдаёт библиотеку целиком (см.
 * `selectElementsPage`).
 */
export function getElementColumns(
    t: TFunction,
    { renderActions }: ElementColumnsOptions = {},
): ColumnDef<Element, unknown>[] {
    return [
        {
            id: "preview",
            header: () => null,
            enableSorting: false,
            enableHiding: false,
            meta: { section: { headClassName: "w-20" } },
            cell: ({ row }) => {
                const cover = row.original.images[0]?.s3Url ?? null;
                return (
                    <div className="h-12 w-16 overflow-hidden rounded bg-muted">
                        {cover ? (
                            <MediaThumb url={cover} kind="image" />
                        ) : (
                            <div className="flex size-full items-center justify-center text-muted-foreground">
                                <Images className="size-4" aria-hidden />
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            id: "name",
            header: t("elements:columns.name"),
            enableSorting: true,
            cell: ({ row }) => (
                <span className="line-clamp-1 max-w-[220px] text-sm font-medium">{row.original.name}</span>
            ),
        },
        {
            id: "description",
            header: t("elements:columns.description"),
            enableSorting: false,
            meta: { section: { hideAt: "md" } },
            cell: ({ row }) => (
                <span className="line-clamp-2 max-w-[320px] text-sm text-muted-foreground">
                    {row.original.description || "—"}
                </span>
            ),
        },
        {
            id: "imagesCount",
            header: t("elements:columns.images"),
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-sm text-muted-foreground tabular-nums">
                    {row.original.images.length}
                </span>
            ),
        },
        {
            id: "source",
            header: t("elements:columns.source"),
            enableSorting: false,
            meta: { section: { hideAt: "lg" } },
            cell: ({ row }) => (
                <Badge variant="outline" className="font-normal">
                    {t(`elements:source.${row.original.source}`)}
                </Badge>
            ),
        },
        {
            id: "createdAt",
            header: t("elements:columns.created"),
            enableSorting: true,
            cell: ({ row }) => (
                <span className="text-sm whitespace-nowrap text-muted-foreground tabular-nums">
                    {fmtRelative(row.original.createdAt)}
                </span>
            ),
        },
        ...(renderActions
            ? [
                  {
                      id: "actions",
                      header: () => null,
                      enableSorting: false,
                      enableHiding: false,
                      meta: { section: { headClassName: "w-12" } },
                      cell: ({ row }: { row: { original: Element } }) => (
                          <div role="presentation" onClick={(e) => e.stopPropagation()}>
                              {renderActions(row.original)}
                          </div>
                      ),
                  } satisfies ColumnDef<Element, unknown>,
              ]
            : []),
    ];
}
